import { isLeft } from "fp-ts/lib/These";
import { inject } from "mobx-react";

import { Remote } from "../../../../core/components/remote/Remote.component";
import { VideoBlockResolver } from "@ihr-radioedit/inferno-core";
import { isPWSSessionBackend, COAST_MESSAGES } from "@inferno/renderer-shared-core";
import type { Store } from "@inferno/renderer-shared-core";
import { getPaths } from "@ihr-radioedit/inferno-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import type { PageBlockInterface } from "../../../../core/page-blocks/Block.component";
import { axiosLoaderWrapper } from "../../lib/coastCommon";
import { CoastMedia } from "./CoastMedia.component";
import { CoastMediaSkeleton } from "./CoastMediaSkeleton.component";
import { coastMediaService } from "../../../../core/services/Pws";

const log = ILog.logger("CoastMediaLoader.component");

interface CoastMediaLoaderProps extends PageBlockInterface {
  store?: Store;
}

export const CoastMediaLoader = inject("store")(({ store, block }: CoastMediaLoaderProps) => {
  const session = store?.session.currentSession;

  if (!store || !isPWSSessionBackend(session)) {
    return null;
  }

  const resolvedBlock = VideoBlockResolver.decode(block.value);

  if (isLeft(resolvedBlock)) {
    const errorMessage = `Video Block Failed Validation\n ${getPaths(resolvedBlock).join("\n")}`;
    log.info(errorMessage);
    return null;
  }

  const { groupid: groupSlug = null, episodeid: episodeSlug = null } = resolvedBlock.right;

  return (
    // double loader so we can re-use cached config
    <Remote
      loader={() =>
        axiosLoaderWrapper(
          () => coastMediaService.getMediaConfig(session.state.accessToken),
          COAST_MESSAGES.ACCOUNT_FETCHING_ERROR,
        )
      }
      fallback={<CoastMediaSkeleton />}
      cacheKey={`coast-media-config`}
      showLoading={true}
    >
      {({ data: configData }) => (
        <Remote
          loader={() =>
            episodeSlug && episodeSlug !== "0"
              ? axiosLoaderWrapper(
                  () => coastMediaService.getMediaEpisodeBySlug(episodeSlug, session.state.accessToken),
                  COAST_MESSAGES.FETCHING_ERROR,
                )
              : Promise.resolve(null)
          }
          cacheKey={`coast-episode-by-slug-${episodeSlug}`}
          fallback={<CoastMediaSkeleton />}
          showLoading={true}
        >
          {({ data: episodeData }) => (
            <CoastMedia
              initialCurEpisodeData={episodeData}
              initialEpisodeSlug={episodeSlug}
              initialConfigData={configData}
              initialGroupSlug={groupSlug}
              session={session}
            />
          )}
        </Remote>
      )}
    </Remote>
  );
});

export default CoastMediaLoader;
